import type {
  AdminConversationDetail,
  AdminConversationMessage,
  AdminConversationSummary,
  EndpointDto,
} from '@agent-platform/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { ChevronDown, ChevronRight, MessagesSquare, Search, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ConfirmDeleteDialog } from '@/components/confirm-delete-dialog';
import { EmptyState } from '@/components/empty-state';
import { ErrorAlert } from '@/components/error-alert';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { api } from '@/lib/api';
import { isLongMessage, splitBlockContext } from '@/lib/conversation-content';
import {
  COST_ESTIMATE_NOTE,
  costCell,
  formatCompact,
  formatDateTime,
  formatRelative,
  formatUsd,
} from '@/lib/format';
import { usePaged } from '@/lib/queries';
import { cn } from '@/lib/utils';

const titleOf = (c: Pick<AdminConversationSummary, 'title'>) => c.title || 'Untitled';

function useDebounced(value: string, ms = 250): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
}

/** Message text with whitespace kept; long text starts collapsed. */
function MessageText({ text, className }: { text: string; className?: string }) {
  const long = isLongMessage(text);
  const [expanded, setExpanded] = useState(false);
  return (
    <div className={cn('grid gap-1', className)}>
      <div
        className={cn(
          'text-sm break-words whitespace-pre-wrap',
          long &&
            !expanded &&
            'max-h-64 overflow-hidden [mask-image:linear-gradient(black_70%,transparent)]',
        )}
      >
        {text}
      </div>
      {long && (
        <button
          type="button"
          className="text-muted-foreground hover:text-foreground justify-self-start text-xs underline"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </div>
  );
}

/** The `<current_block>` context Blocks Studio sends with each message, collapsed. */
function BlockContext({ context }: { context: string }) {
  const [open, setOpen] = useState(false);
  const Icon = open ? ChevronDown : ChevronRight;
  return (
    <div className="grid gap-1">
      <button
        type="button"
        aria-expanded={open}
        className="text-muted-foreground hover:text-foreground flex items-center gap-1 text-xs"
        onClick={() => setOpen((v) => !v)}
      >
        <Icon className="size-3" aria-hidden />
        Block context
        <span className="tabular-nums">· {formatCompact(context.length)} chars</span>
      </button>
      {open && (
        <pre className="bg-background max-h-80 overflow-auto rounded-md border p-2 font-mono text-[11px] break-all whitespace-pre-wrap">
          {context}
        </pre>
      )}
    </div>
  );
}

function TranscriptMessage({ message }: { message: AdminConversationMessage }) {
  const { text, context } = splitBlockContext(message.content);
  const user = message.role === 'user';
  return (
    <li className="grid gap-1.5" data-testid="conversation-message">
      <div className="text-muted-foreground flex items-center gap-2 text-xs">
        <span className={cn('font-medium', user ? 'text-foreground' : 'text-primary')}>
          {user ? 'User' : 'Assistant'}
        </span>
        <span>{formatDateTime(message.createdAt)}</span>
        {!user && message.runId && (
          <Link
            to="/traces/runs/$runId"
            params={{ runId: message.runId }}
            className="hover:text-foreground font-mono underline-offset-2 hover:underline"
          >
            run {message.runId.slice(-8)}
          </Link>
        )}
        {!user && message.costUsd !== null && (
          <span title={COST_ESTIMATE_NOTE}>{formatUsd(message.costUsd)}</span>
        )}
      </div>
      <div
        className={cn(
          'grid gap-2 rounded-lg border px-3 py-2',
          user ? 'bg-accent/60' : 'bg-muted/40',
        )}
      >
        {text ? (
          <MessageText text={text} />
        ) : (
          <span className="text-muted-foreground text-sm italic">(no text)</span>
        )}
        {context !== null && <BlockContext context={context} />}
      </div>
    </li>
  );
}

function ConversationPanel({
  tenantId,
  endpointId,
  conversationId,
  canDelete,
  onClose,
}: {
  tenantId: string;
  endpointId: string;
  conversationId: string | null;
  canDelete: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const detail = useQuery({
    queryKey: ['endpoint-conversation', tenantId, endpointId, conversationId],
    queryFn: () =>
      api<AdminConversationDetail>(`/endpoints/${endpointId}/conversations/${conversationId}`),
    enabled: Boolean(conversationId),
  });
  const [confirming, setConfirming] = useState(false);
  const remove = useMutation({
    mutationFn: () =>
      api<void>(`/endpoints/${endpointId}/conversations/${conversationId}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast.success('Conversation deleted');
      void queryClient.invalidateQueries({
        queryKey: ['endpoint-conversations', tenantId, endpointId],
      });
      onClose();
    },
  });
  const c = detail.data;
  const detailCost = costCell(c?.costUsd ?? 0, c?.unpricedRuns ?? 0);

  return (
    <Sheet open={Boolean(conversationId)} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="flex w-full flex-col gap-0 sm:max-w-2xl">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2 pr-6">
            <MessagesSquare className="size-4 shrink-0" aria-hidden />
            <span className="truncate">{c ? titleOf(c) : 'Conversation'}</span>
          </SheetTitle>
          <SheetDescription>
            {c ? (
              <>
                User <span className="font-mono">{c.userKey}</span>
                {c.scopeKey && (
                  <>
                    {' · '}scope <span className="font-mono">{c.scopeKey}</span>
                  </>
                )}
                {` · ${c.messageCount} messages · ~${formatCompact(c.approxTokens)} tokens`}
                <span
                  title={conversationCostTitle(detailCost.title)}
                >{` · ${detailCost.text}`}</span>
                {` · active ${formatRelative(c.lastActiveAt)}`}
              </>
            ) : (
              'Loading…'
            )}
          </SheetDescription>
        </SheetHeader>
        <div className="grid min-h-0 flex-1 content-start gap-4 overflow-y-auto px-4 pb-4">
          <ErrorAlert error={detail.error} />
          {!c ? (
            !detail.error && <Skeleton className="h-40" />
          ) : (
            <>
              {c.messages.length === 0 ? (
                <p className="text-muted-foreground text-sm">No stored messages.</p>
              ) : (
                <ol className="grid gap-4" aria-label="Messages">
                  {c.messages.map((m) => (
                    <TranscriptMessage key={m.id} message={m} />
                  ))}
                </ol>
              )}
              {canDelete && (
                <Button
                  variant="destructive"
                  className="justify-self-start"
                  onClick={() => setConfirming(true)}
                >
                  <Trash2 aria-hidden /> Delete conversation
                </Button>
              )}
            </>
          )}
        </div>
        <ConfirmDeleteDialog
          open={confirming}
          onOpenChange={setConfirming}
          title="Delete this conversation?"
          description="Its messages are deleted for good. The user's application can no longer continue or reopen it."
          onConfirm={() => remove.mutateAsync()}
        />
      </SheetContent>
    </Sheet>
  );
}

/** The endpoint's stored conversations, across every user and scope. */
/** Conversation costs only include successful turns; failed runs are never added. */
const conversationCostTitle = (title: string | undefined) =>
  `${title ?? COST_ESTIMATE_NOTE}. Only successful turns are counted.`;

export function EndpointConversations({
  tenantId,
  endpoint,
  canDelete,
}: {
  tenantId: string;
  endpoint: EndpointDto;
  canDelete: boolean;
}) {
  const [userKey, setUserKey] = useState('');
  const [scopeKey, setScopeKey] = useState('');
  const [q, setQ] = useState('');
  const filters = {
    userKey: useDebounced(userKey.trim()),
    scopeKey: useDebounced(scopeKey.trim()),
    q: useDebounced(q.trim()),
  };
  const params = new URLSearchParams();
  if (filters.userKey) params.set('userKey', filters.userKey);
  if (filters.scopeKey) params.set('scopeKey', filters.scopeKey);
  if (filters.q) params.set('q', filters.q);
  const query = params.toString();
  const conversations = usePaged<AdminConversationSummary>(
    ['endpoint-conversations', tenantId, endpoint.id, query],
    `/endpoints/${endpoint.id}/conversations${query ? `?${query}` : ''}`,
  );
  const rows = conversations.data?.pages.flatMap((p) => p.items) ?? [];
  const [open, setOpen] = useState<string | null>(null);
  const history = endpoint.conversationHistory;

  return (
    <div className="grid gap-4">
      {!history.enabled && (
        <Alert>
          <AlertDescription>
            Conversation history is off for this endpoint — turn it on in Settings.
          </AlertDescription>
        </Alert>
      )}
      <div className="flex flex-wrap items-end gap-3">
        <div className="grid gap-1.5">
          <Label htmlFor="conv-user">User</Label>
          <Input
            id="conv-user"
            className="w-56 font-mono text-xs"
            placeholder={history.userHeader}
            value={userKey}
            onChange={(e) => setUserKey(e.target.value)}
          />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="conv-scope">Scope</Label>
          <Input
            id="conv-scope"
            className="w-48 font-mono text-xs"
            placeholder={history.scopeHeader ?? 'any'}
            value={scopeKey}
            onChange={(e) => setScopeKey(e.target.value)}
          />
        </div>
        <div className="relative grid gap-1.5">
          <Label htmlFor="conv-q">Title</Label>
          <Search
            className="text-muted-foreground pointer-events-none absolute bottom-2 left-3 size-4"
            aria-hidden
          />
          <Input
            id="conv-q"
            type="search"
            className="w-64 pl-9"
            placeholder="Search titles"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
      </div>
      <p className="text-muted-foreground -mt-2 text-xs">
        User and scope match exactly (the values sent in the endpoint&apos;s headers).
      </p>
      <ErrorAlert error={conversations.error} />
      {conversations.isPending ? (
        <Skeleton className="h-40" />
      ) : rows.length === 0 ? (
        <EmptyState title={query ? 'No matching conversations' : 'No conversations yet'}>
          {query
            ? 'Try another user, scope or title.'
            : `Conversations appear here once a request sends the ${history.userHeader} header.`}
        </EmptyState>
      ) : (
        <Card>
          <CardContent className="pt-6">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Scope</TableHead>
                  <TableHead className="text-right">Messages</TableHead>
                  <TableHead className="text-right">Cost</TableHead>
                  <TableHead className="text-right">Last active</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((c) => (
                  <TableRow
                    key={c.id}
                    className="cursor-pointer"
                    data-testid="conversation-row"
                    onClick={() => setOpen(c.id)}
                  >
                    <TableCell className="max-w-72 truncate">
                      <button
                        type="button"
                        className={cn(
                          'hover:underline',
                          !c.title && 'text-muted-foreground italic',
                        )}
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpen(c.id);
                        }}
                      >
                        {titleOf(c)}
                      </button>
                    </TableCell>
                    <TableCell className="max-w-48 truncate font-mono text-xs">
                      {c.userKey}
                    </TableCell>
                    <TableCell className="text-muted-foreground max-w-40 truncate font-mono text-xs">
                      {c.scopeKey || '—'}
                    </TableCell>
                    <TableCell className="text-right text-xs tabular-nums">
                      {c.messageCount}
                    </TableCell>
                    <TableCell
                      className="text-right text-xs whitespace-nowrap tabular-nums"
                      title={conversationCostTitle(costCell(c.costUsd, c.unpricedRuns).title)}
                    >
                      {costCell(c.costUsd, c.unpricedRuns).text}
                    </TableCell>
                    <TableCell
                      className="text-muted-foreground text-right text-xs whitespace-nowrap"
                      title={formatDateTime(c.lastActiveAt)}
                    >
                      {formatRelative(c.lastActiveAt)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {conversations.hasNextPage && (
              <div className="mt-4 flex justify-center">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={conversations.isFetchingNextPage}
                  onClick={() => void conversations.fetchNextPage()}
                >
                  {conversations.isFetchingNextPage ? 'Loading…' : 'Load more'}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}
      <ConversationPanel
        tenantId={tenantId}
        endpointId={endpoint.id}
        conversationId={open}
        canDelete={canDelete}
        onClose={() => setOpen(null)}
      />
    </div>
  );
}
